import { useEffect, useRef } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { ID } from '@ianesthesie-kit/services';
import { useAppDispatch } from '@/hooks';
import { doctorBffApi } from '../doctorBffApi.slice';
import { useTerminalReadersQuery } from './billingEntity.slice';
import { useIsBillingStripeEnabled } from './billingLine.hooks';
import { BillingLineDto } from './billingLine.model';
import { useBillingLinesQuery } from './billingLine.slice';
import { DeskPayment } from './deskPayment.model';
import {
  DESK_PAYMENT_RESUME_ACTION_KEY,
  DESK_PAYMENT_START_ACTION_KEY,
  deskPaymentIneligibilityLabelKey
} from './deskPayment.labels';
import {
  deskPaymentApi,
  deskPaymentCaptureTags,
  useDeskPaymentsByPatientQuery
} from './deskPayment.slice';
import {
  DeskPaymentLineTarget,
  DeskPaymentSettleLineTarget,
  deskPaymentCapturedLineIds,
  findHoldToResolveForLine,
  findLockingDeskPaymentForLine,
  findPendingDeskPaymentForLine,
  findSettleLineForOutcome,
  hasActiveDeskPaymentChain,
  isDeskPaymentCaptured,
  isResolvableHold
} from './deskPayment.utils';

/** Fast enough to follow a tap from the rows, slow enough for a file left open on screen. */
const LIVE_PENDING_POLLING_MS = 5000;

/** A live hold moves only on a sweeper cycle; one modest read discovers that transition. */
const LIVE_HOLD_POLLING_MS = 60000;

/**
 * A patient's card-terminal payments, behind one cache entry shared by every surface that
 * shows a line of that patient — the PDV receivables rows, the chase drawer's line cards, and
 * the dialog / capture watch each of those surfaces runs — so the polling rule is stated once
 * and the rows of one patient cost one request, however many of them are on screen.
 *
 * Re-read while a reader payment or hold renewal is still live. This GET cannot move a payment
 * (only `POST /refresh` can, which `DeskPaymentRegistryRefreshers` runs for the payments this
 * browser registered): it is how an outcome obtained elsewhere — another tab, another desk, a
 * payment nobody here started — is ever noticed. Reader polling is bounded by the 20-minute
 * pending horizon. A held row re-reads more slowly so the page discovers the sweeper entering
 * renewal; RENEWING then uses the fast interval until the next pass replaces or ends it. None
 * of these list reads sends a released intent to `/refresh`.
 *
 * Not issued at all without a patient or on a hospital without Stripe.
 */
export const useDeskPaymentsForPatient = (patientId: ID | undefined) => {
  const stripeEnabled = useIsBillingStripeEnabled();
  const arg = patientId && stripeEnabled ? { patientId } : skipToken;

  // Read from the store rather than from the subscription below: the interval is an input of
  // the very query that answers the question, so the flag can only ever come from a round that
  // has already landed — and a non-subscribing read of the cache entry gets it as a plain
  // derivation, where reading it off the subscription's own result would be circular.
  // Deliberately not memoised on the data: the age bound is a function of time, and RTK's
  // structural sharing keeps `currentData`'s identity stable across identical polls, so a
  // memo keyed on it would never observe the horizon on a row nothing else touches.
  const { currentData } =
    deskPaymentApi.endpoints.deskPaymentsByPatient.useQueryState(arg);
  const hasActiveChain = hasActiveDeskPaymentChain(currentData, Date.now());
  const hasLiveHold = (currentData ?? []).some(
    (payment) =>
      payment.status === 'PARTIALLY_CAPTURED' && isResolvableHold(payment)
  );

  return useDeskPaymentsByPatientQuery(arg, {
    pollingInterval: hasActiveChain
      ? LIVE_PENDING_POLLING_MS
      : hasLiveHold
      ? LIVE_HOLD_POLLING_MS
      : undefined,
    skipPollingIfUnfocused: true
  });
};

/**
 * Whether the hospital has a card reader at all, whatever its ONLINE/OFFLINE status — an
 * offline reader is usually only asleep, and the dialog dims it but still allows it. Answered
 * hospital-wide: the line's billing doctor's entity cannot be resolved client-side, so the
 * dialog remains the backstop for NO_BILLING_ENTITY / ENTITY_NOT_ONBOARDED and for that
 * entity owning no reader of its own.
 *
 * Fail-open like {@link useDeskPaymentLock}: `undefined` — loading, failed, or a hospital
 * without Stripe — means "not known", and only a known-empty list is an answer a caller may
 * bar its action on. One request whatever the number of rows asking, RTK dedupes the query.
 */
export const useHasTerminalReader = (): boolean | undefined => {
  const stripeEnabled = useIsBillingStripeEnabled();
  const { currentData } = useTerminalReadersQuery(undefined, {
    skip: !stripeEnabled
  });
  return currentData ? currentData.length > 0 : undefined;
};

/**
 * What the surface knows about collecting the line, which the payments alone cannot say: the
 * facts {@link DeskPaymentLineAffordance.canStartDeskPayment} is built from.
 */
export type DeskPaymentLineAffordanceInput = {
  /** `useBillingLineActions.canCollect`: OPEN, a balance left, and nothing holding the line. */
  canCollect: boolean;
  /**
   * For an acte: whether its consultation is settled, so the acte may be taken on its own.
   * While that consultation is owed, its own payment collects both and the backend refuses an
   * acte-only start with `CONSULTATION_STILL_OPEN`. Read on actes alone — the other line types
   * are never collected as a pair.
   */
  acteCollectableAlone: boolean;
  /**
   * Whether `payments` is an answer rather than a read still in flight. The patient-scoped
   * surfaces read a list that may simply not have loaded, and an unloaded list is not an empty
   * one: taking it for "nothing live" would offer a second payment on a line already carrying
   * one. A chase-dashboard row carries its own payment, where "none" *is* the answer.
   */
  paymentsKnown: boolean;
};

/** What a line's card-terminal controls offer, and how they are worded. */
export type DeskPaymentLineAffordance = {
  /**
   * Whether a *new* payment may be started on the line. Both line types are collected at the
   * desk: the consultation on the CPA day (with the intervention held on top of it where the
   * card allows), the acte on its own once the patient is back after surgery. `canCollect` is
   * already false under a lock or a live hold, which is what keeps a second payment off a line
   * the backend would refuse it on (`E1-12-2-1078`).
   */
  canStartDeskPayment: boolean;
  /**
   * The payment live on a reader for this line: the control then walks back to it instead of
   * offering a second one, which the backend's own eligibility check would refuse.
   */
  pendingDeskPayment: DeskPayment | undefined;
  /**
   * The hold this line states through its pill, and therefore the one its outcome control
   * resolves — only the row rendering a hold offers to resolve it, so the control cannot
   * appear twice for one payment.
   */
  holdToResolve: DeskPayment | undefined;
  /** The control's own wording: start a payment, or resume the live one. */
  deskPaymentActionKey: string;
  /**
   * Barred before the dialog when the hospital has no reader to send the tap to. Never on the
   * resume path: a live payment already holds the reader it was started on. Only a known-empty
   * list bars it (see {@link useHasTerminalReader}), and the dialog still answers the
   * entity-scoped refusals this cannot see.
   */
  noReaderToPayOn: boolean;
  /** The refusal when there is one, the action's own wording otherwise. */
  deskPaymentTooltipKey: string;
};

/**
 * What the card-terminal controls of one line offer, stated once for the surfaces that carry
 * them — the PDV receivables row, the chase dashboard's action cell and its patient drawer's
 * line card — so a hold resolved from one is offered on the same rule as from the others. The
 * payments come from the caller: the patient-scoped surfaces derive them from their shared
 * list, the dashboard row carries its own, and a per-row query here would fan one GET out per
 * row of a 50-row page.
 */
export const useDeskPaymentLineAffordance = (
  line: DeskPaymentLineTarget,
  payments: DeskPayment[] | undefined,
  {
    canCollect,
    acteCollectableAlone,
    paymentsKnown
  }: DeskPaymentLineAffordanceInput
): DeskPaymentLineAffordance => {
  const hasTerminalReader = useHasTerminalReader();

  const pendingDeskPayment = findPendingDeskPaymentForLine(payments, line);
  const deskPaymentActionKey = pendingDeskPayment
    ? DESK_PAYMENT_RESUME_ACTION_KEY
    : DESK_PAYMENT_START_ACTION_KEY;
  const noReaderToPayOn = hasTerminalReader === false && !pendingDeskPayment;

  return {
    canStartDeskPayment:
      canCollect &&
      paymentsKnown &&
      (line.lineType !== 'ACTE' || acteCollectableAlone),
    pendingDeskPayment,
    holdToResolve: findHoldToResolveForLine(payments, line),
    deskPaymentActionKey,
    noReaderToPayOn,
    deskPaymentTooltipKey: noReaderToPayOn
      ? deskPaymentIneligibilityLabelKey('NO_READER')
      : deskPaymentActionKey
  };
};

/** What the outcome dialog prices its final capture against, and whether it is known yet. */
export type OutcomeSettleLineState = {
  settleLine: DeskPaymentSettleLineTarget | null;
  isLoading: boolean;
  /**
   * The lines could not be read: `settleLine` is then `null` for want of an answer, not for
   * want of a line, and the dialog must not word it as "this intervention has no devis yet".
   */
  isError: boolean;
  refetch: () => void;
};

/**
 * The acte line a hold's outcome settles, resolved from the patient's own lines rather than
 * from whatever the surface that opened the dialog has on screen. The hold is stated on the
 * consultation stand-in as often as on the acte itself, and the chase dashboard — a page of
 * rows narrowed by a status tab — structurally cannot hold the acte line of a consultation it
 * is showing: reading it off those rows leaves the dialog with nothing to price against on the
 * very tab the stand-in appears on (PAID, where a collected consultation lands).
 *
 * One query, and on the patient file not even that: it is the same cache entry the receivables
 * block already subscribes to, so the line is there before the dialog paints.
 */
export const useOutcomeSettleLine = (
  payment: DeskPayment,
  patientId: ID
): OutcomeSettleLineState => {
  const {
    currentData: lines,
    isLoading,
    isUninitialized,
    isError,
    refetch
  } = useBillingLinesQuery({ patientId });
  return {
    settleLine: findSettleLineForOutcome(payment, lines),
    // `isUninitialized` is not an answer either: without it the render before the first request
    // is even issued reports "no acte line", which is what the dialog bars its outcome on.
    isLoading: isLoading || isUninitialized,
    isError,
    refetch
  };
};

/** What the lock reads: whose payments to look at, and which of them hold this line. */
export type DeskPaymentLockTarget = DeskPaymentLineTarget &
  Pick<BillingLineDto, 'patientId'>;

export type DeskPaymentLockState = {
  /**
   * The patient's card payments, or `undefined` while they load, on a hospital without Stripe
   * and when the read is disabled — callers read that as "not known yet", which is not the
   * same answer as "none".
   */
  deskPayments: DeskPayment[] | undefined;
  /** The card payment holding this line, if one is known to. */
  deskPaymentLock: DeskPayment | undefined;
  isLocked: boolean;
};

/**
 * The frontend's copy of the backend's own lock on a line (see
 * {@link findLockingDeskPaymentForLine}): while a card payment holds it, medical-synthesis
 * refuses every amount-moving mutation with `E1-12-2-1072` until that payment is finished or
 * canceled, and the chase paths with it (`BC_REMINDER_NOT_ALLOWED`: reminding would ask the
 * patient twice for money already held on the card) — so the surfaces offer none of them
 * rather than letting the secretary discover the refusal.
 *
 * Deliberately fail-open: an unloaded list yields no lock, since fail-closed would block all
 * cash collection whenever the endpoint is down, and the backend guards every mutation anyway.
 *
 * The list comes back with the lock because both are one read: a caller needing the payments
 * themselves (a row's pill, its "resume" affordance) must not issue a second query for them.
 */
export const useDeskPaymentLock = (
  line: DeskPaymentLockTarget,
  /** Off on a surface that would fan the patient-scoped query out per row. */
  enabled = true
): DeskPaymentLockState => {
  const { currentData: deskPayments } = useDeskPaymentsForPatient(
    enabled ? line.patientId : undefined
  );
  const deskPaymentLock = findLockingDeskPaymentForLine(deskPayments, line);
  return { deskPayments, deskPaymentLock, isLocked: !!deskPaymentLock };
};

/**
 * Closes an editor a card payment has just barred. A colleague's tap at another desk can lock
 * the line within one poll window, form open; the backend would then refuse the submit
 * outright, so the in-place amount editors of the PDV row and the drawer card close instead of
 * inviting a doomed patch.
 */
export const useCloseOnLock = (isLocked: boolean, close: () => void) => {
  // Read through a ref so a caller can pass its closer inline: only the lock appearing is
  // meant to fire this, never the identity of a callback rebuilt on every render.
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  }, [close]);

  useEffect(() => {
    if (isLocked) {
      closeRef.current();
    }
  }, [isLocked]);
};

/** The two caches to reconcile, and the one payment somebody else is already answering for. */
export type DeskPaymentCaptureReconciliationInput = {
  lines: BillingLineDto[] | undefined;
  /** The lines query's own `isFetching` / `startedTimeStamp`. */
  linesFetching: boolean;
  linesStartedAt: number | undefined;
  deskPayments: DeskPayment[] | undefined;
  /** The payment a tracker on this surface is actively polling, if any. */
  followedPaymentIntentId?: ID | null;
};

/**
 * Reconciles a capture the desk payments know about but the billing lines do not, by
 * invalidating what that capture changed. A capture witnessed by a `/refresh` poll already does
 * so (see `deskPaymentRegistry.listener.ts`), but a surface can hold both caches with nobody
 * witnessing anything: a payment already captured when the file was opened, and a capture the
 * list poll reveals of a payment no dialog here is following.
 */
export const useDeskPaymentCaptureReconciliation = ({
  lines,
  linesFetching,
  linesStartedAt,
  deskPayments,
  followedPaymentIntentId
}: DeskPaymentCaptureReconciliationInput) => {
  const dispatch = useAppDispatch();

  /**
   * The lines-cache generation each capture has been reported against — the request in flight
   * (or last started) when the payment was first seen captured here. Asking again once a newer
   * one has started would be asking the same question twice; asking again while that generation
   * still stands is a race, and worth one more invalidation. Bounded by
   * construction: a line legitimately left OPEN under a capture (a total raised afterwards)
   * cannot re-arm this, since the very refetch it triggered supersedes it.
   */
  const capturesReported = useRef(new Map<ID, number>());

  useEffect(() => {
    // A refetch already on its way is the answer to this question — its response settles
    // whether the line is really still open.
    if (linesFetching) {
      return;
    }
    const generation = linesStartedAt ?? 0;
    for (const payment of deskPayments ?? []) {
      // Every line the payment has captured on, not just the consultation: a two-bite hold
      // settled at another desk pays the acte line while the consultation has been PAID for
      // days, so the narrower question would answer "nothing to reconcile" on the very case
      // this exists for — and an acte-only capture would match no line of the list at all.
      const capturedLineIds = deskPaymentCapturedLineIds(payment);
      const lineStillOpen = (lines ?? []).some(
        (line) =>
          line.storedStatus === 'OPEN' && capturedLineIds.includes(line.id)
      );
      const reportedAt = capturesReported.current.get(payment.paymentIntentId);
      if (
        !isDeskPaymentCaptured(payment.status) ||
        !lineStillOpen ||
        // The tracker following this payment reports its own outcome, on a faster poll: the
        // list is simply the first of the two to have said so.
        payment.paymentIntentId === followedPaymentIntentId ||
        (reportedAt !== undefined && generation > reportedAt)
      ) {
        continue;
      }
      capturesReported.current.set(payment.paymentIntentId, generation);
      dispatch(
        doctorBffApi.util.invalidateTags(deskPaymentCaptureTags(payment))
      );
    }
  }, [
    deskPayments,
    lines,
    linesFetching,
    linesStartedAt,
    followedPaymentIntentId,
    dispatch
  ]);
};
